import { useState } from 'react';
import { Plus, X } from 'lucide-react';
import { Button, Input, DatePicker } from '@roger/ui-components';
import { formatDateToISO, parseISOToLocalDate } from '../../../utils/dateUtils';
import { MAX_UB04_SERVICE_LINES, type UB04ServiceLine } from './types';

const newRowId = (): string =>
  globalThis.crypto?.randomUUID?.() ?? '00000000-0000-4000-8000-000000000000';

const emptyManualLine = (): UB04ServiceLine => ({
  row_id: newRowId(),
  source_type: 'manual',
  source_id: null,
  row_kind: 'manual',
  revenue_code: '',
  revenue_description_or_ide: '',
  hcpcs_hipps_and_modifiers: '',
  service_date: null,
  units: '1',
  charges: '0.00',
  noncovered_charges: '0.00',
});

export function ServiceLinesSection({
  value,
  onChange,
  disabled = false,
}: {
  value: UB04ServiceLine[];
  onChange: (next: UB04ServiceLine[]) => void;
  disabled?: boolean;
}) {
  const [error, setError] = useState<string | null>(null);

  const update = (index: number, patch: Partial<UB04ServiceLine>) => {
    onChange(
      value.map((line, i) => (i === index ? { ...line, ...patch } : line))
    );
  };

  const addLine = () => {
    if (value.length >= MAX_UB04_SERVICE_LINES) {
      setError('FL 42-48 supports 22 service lines.');
      return;
    }
    setError(null);
    onChange([...value, emptyManualLine()]);
  };

  const removeLine = (index: number) => {
    setError(null);
    onChange(value.filter((_, i) => i !== index));
  };

  return (
    <section className='space-y-3'>
      <div>
        <h3 className='text-sm font-semibold'>Service lines</h3>
        <p className='text-xs text-muted-foreground'>
          FL 42-48. Saved UB-04 preview supports one page, up to 22 lines.
        </p>
      </div>
      {error ? <p className='text-sm text-red-600'>{error}</p> : null}
      <div className='space-y-2'>
        {value.map((line, index) => (
          <div
            key={line.row_id ?? `manual-${index}`}
            className='grid gap-2 rounded border p-3 md:grid-cols-[4rem_minmax(8rem,1fr)_8rem_8rem_6rem_6rem_2rem]'
          >
            <label className='space-y-1 text-xs text-muted-foreground'>
              Rev
              <Input
                inputSize='sm'
                value={line.revenue_code}
                disabled={disabled}
                onChange={event =>
                  update(index, { revenue_code: event.target.value })
                }
              />
            </label>
            <label className='space-y-1 text-xs text-muted-foreground'>
              Description
              <Input
                inputSize='sm'
                maxLength={24}
                value={line.revenue_description_or_ide}
                disabled={disabled}
                onChange={event =>
                  update(index, {
                    revenue_description_or_ide: event.target.value,
                  })
                }
              />
            </label>
            <label className='space-y-1 text-xs text-muted-foreground'>
              HCPCS/HIPPS
              <Input
                inputSize='sm'
                value={line.hcpcs_hipps_and_modifiers ?? ''}
                disabled={disabled}
                onChange={event =>
                  update(index, {
                    hcpcs_hipps_and_modifiers: event.target.value,
                  })
                }
              />
            </label>
            <label className='space-y-1 text-xs text-muted-foreground'>
              Date
              <DatePicker
                datePickerSize='sm'
                typable
                date={parseISOToLocalDate(line.service_date ?? '')}
                disabled={disabled}
                onDateChange={date =>
                  update(index, {
                    service_date: date ? formatDateToISO(date) : null,
                  })
                }
              />
            </label>
            <label className='space-y-1 text-xs text-muted-foreground'>
              Units
              <Input
                inputSize='sm'
                inputMode='decimal'
                value={String(line.units ?? '')}
                disabled={disabled}
                onChange={event => update(index, { units: event.target.value })}
              />
            </label>
            <label className='space-y-1 text-xs text-muted-foreground'>
              Charges
              <Input
                inputSize='sm'
                inputMode='decimal'
                value={String(line.charges ?? '')}
                disabled={disabled}
                onChange={event =>
                  update(index, { charges: event.target.value })
                }
              />
            </label>
            <div className='flex items-end justify-end'>
              <Button
                type='button'
                variant='ghost'
                size='icon'
                aria-label='Remove service line'
                disabled={disabled || line.source_type !== 'manual'}
                onClick={() => removeLine(index)}
              >
                <X className='h-4 w-4' />
              </Button>
            </div>
            <div className='md:col-span-7 text-xs text-muted-foreground'>
              Source: {line.source_type}
              {line.source_id ? ` · ${line.source_id}` : ''}
            </div>
          </div>
        ))}
      </div>
      <Button
        type='button'
        variant='outline'
        size='sm'
        onClick={addLine}
        disabled={disabled}
      >
        <Plus className='mr-1 h-3 w-3' />
        Add service line
      </Button>
    </section>
  );
}
